import React from "react";
import { Helmet } from "react-helmet-async";

const StructuredData = ({ data }) => {
  if (!data) return null;

  // Handle both single schema objects and arrays of schemas
  const schemas = Array.isArray(data) ? data : [data];

  return (
    <Helmet>
      {schemas.map((schema, index) => (
        <script
          key={index}
          type="application/ld+json"
        >
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

// Helper function to create Organization schema
export const createOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Estabild",
  legalName: "Estabild AB",
  url: "https://www.estabild.com",
  logo: "https://www.estabild.com/logo512.png",
  description:
    "Estabild provides intelligent reporting and project management solutions for real estate development projects. We help businesses streamline and automate business processes to ensure predictable outcomes.",
  address: {
    "@type": "PostalAddress",
    addressCountry: "SE",
  },
  contactPoint: {
    "@type": "ContactPoint",
    email: "hello@estabild.com",
    contactType: "Customer Service",
  },
  sameAs: [
    "https://www.linkedin.com/company/estabild",
    "https://twitter.com/estabild",
  ],
  foundingDate: "2020",
  numberOfEmployees: {
    "@type": "QuantitativeValue",
    value: "10-50",
  },
});

// Helper function to create WebSite schema
export const createWebSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Estabild",
  url: "https://www.estabild.com",
  description:
    "Automated reporting and project management for real estate development projects",
  publisher: {
    "@type": "Organization",
    name: "Estabild AB",
  },
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: "https://www.estabild.com/search?q={search_term_string}",
    },
    "query-input": "required name=search_term_string",
  },
});

// Helper function to create BreadcrumbList schema
export const createBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: `https://www.estabild.com${item.url}`,
  })),
});

// Helper function to create Service schema
export const createServiceSchema = (serviceName, description, url) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: serviceName,
  description: description,
  provider: {
    "@type": "Organization",
    name: "Estabild",
    url: "https://www.estabild.com",
  },
  areaServed: {
    "@type": "Country",
    name: "Global",
  },
  url: `https://www.estabild.com${url}`,
  serviceType: "Software as a Service",
  category: "Project Management Software",
});

// Helper function to create FAQPage schema
export const createFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
});

// Helper function to create SoftwareApplication schema
export const createSoftwareApplicationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Estabild",
  applicationCategory: "ProjectManagementApplication",
  operatingSystem: "Web",
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
  },
  description:
    "Intelligent reporting and project management platform for real estate development projects",
  url: "https://www.estabild.com",
  provider: {
    "@type": "Organization",
    name: "Estabild AB",
  },
});

export default StructuredData;

